import React from "react";
import {
  View,
  StyleSheet,
  Image,
  FlatList,
  ScrollView,
  ActivityIndicator,
  Modal,
  TouchableOpacity,
  Pressable,
  Platform,
  useWindowDimensions,
  Alert,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

import { SessionContext } from "../../../../contexts";
import { InputDropdown, NormalText, TitleText } from "../../../common/native";
import {
  TGetBeforeAfterResponse,
  assignPatientToSideBySideBatch,
  deleteProgressPhoto,
  getBeforeAfter,
  getPatients,
  TPatientInfo,
} from "../../../../services/api-clinic";
import ImageCarousel from "./ImageCarousel";
import { Download, Xv2 } from "../../../../assets";
import {
  createUUID,
  getS3KeyFromPresignedUrlForGallery,
} from "../../../../utils/functions";
import { mixpanel } from "../../../../utils/mixpanel";
import { Paginate } from "../../../common/web";
import GalleryEmpty from "./GalleryEmpty";
import AssignImageToPatientModal from "../../../uncommon/native/modals/AssignImageToPatientModal";

type GalleryProps = {};
type TGalleryData = {
  batch_uuid: string[];
  patient_uuid: (string | null)[];
  patient_name: (string | null)[];
  patient_info: (TPatientInfo | null)[];
  date_created: string;
  uploaded: string[][];
  generated: string[][];
  uploadedThumbnails: string[][];
  generatedThumbnails: string[][];
}[];

const LIMIT = 10;

// TODO: Refactor this component into smaller components
const Gallery: React.FC<GalleryProps> = () => {
  const session = React.useContext(SessionContext)?.session;
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 644;

  const [error, setError] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadingModal, setLoadingModal] = React.useState<boolean>(false);
  const [errorModal, setErrorModal] = React.useState<string>("");
  const [imageDisplayOption, setImageDisplayOption] =
    React.useState<string>("Generated");
  const [galleryData, setGalleryData] = React.useState<TGalleryData>([]);
  const [selectedImages, setSelectedImages] = React.useState<string[]>([]);
  const [modalVisible, setModalVisible] = React.useState<boolean>(false);
  const [selectedBatchUUID, setSelectedBatchUUID] = React.useState<
    string | null
  >(null);
  const [selectedImageOption, setSelectedImageOption] = React.useState<
    "Generated" | "Uploaded"
  >("Generated");
  const [selectedModalPatientInfo, setSelectedModalPatientInfo] =
    React.useState<TPatientInfo | null>(null);
  const [isAssignModalVisible, setIsAssignModalVisible] =
    React.useState<boolean>(false);
  const [assignTargetBatchUuid, setAssignTargetBatchUuid] = React.useState<
    string | null
  >(null);
  const [patientInfoByBatchUuid, setPatientInfoByBatchUuid] = React.useState<
    Record<string, TPatientInfo | null>
  >({});

  const [pageCount, setPageCount] = React.useState<number>(0);
  const [selectedPage, setSelectedPage] = React.useState<number>(0);

  React.useEffect(() => {
    mixpanel.track("Before/After Gallery Page Viewed", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const fetchData = async (page: number) => {
    setLoading(true);
    try {
      console.log("Fetching data for page:", page);
      console.log("Session:", session);
      if (!session) return;

      const url = new URL(window.location.href);
      url.searchParams.set("page", page.toString());
      window.history.pushState({}, "", url.toString());

      const response: TGetBeforeAfterResponse = await getBeforeAfter(session, {
        is_uploaded: true,
        limit: LIMIT,
        offset: page * LIMIT,
      });
      console.log("Response:", response);

      for (const batch_image of response.batch_images) {
        if (batch_image.upload_types.length !== batch_image.urls.length) {
          throw new Error("Upload types and urls length mismatch");
        }
      }

      setPageCount(response.page_count);
      setSelectedPage(page);

      const result = response.batch_images.sort((a, b) => {
        return (
          new Date(b.date_created).getTime() -
          new Date(a.date_created).getTime()
        );
      });

      const uniquePatientUuids = Array.from(
        new Set(
          result
            .map((item) => item.patient_uuid)
            .filter(
              (patientUuid): patientUuid is string =>
                typeof patientUuid === "string" && patientUuid.length > 0,
            ),
        ),
      );

      const patientByUuid = new Map<string, TPatientInfo>();
      const patientNameByUuid = new Map<string, string>();
      if (uniquePatientUuids.length > 0) {
        const patients = await getPatients(session, {
          patientIds: uniquePatientUuids,
        });
        patients.forEach((patient) => {
          patientByUuid.set(patient.uuid, patient);
          const fullName = `${patient.firstName || ""} ${
            patient.lastName || ""
          }`.trim();
          if (patient.uuid && fullName) {
            patientNameByUuid.set(patient.uuid, fullName);
          }
        });
      }

      const aggregatedCreatedDate = result.reduce(
        (acc, batch_image) => {
          const date = new Date(batch_image.date_created);
          const formattedDate = date.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          if (!acc[formattedDate]) {
            acc[formattedDate] = {
              date_created: formattedDate,
              uploaded: [],
              generated: [],
              uploadedThumbnails: [],
              generatedThumbnails: [],
              batch_uuid: [],
              patient_uuid: [],
              patient_name: [],
              patient_info: [],
            };
          }

          const uploadedRow: string[] = [];
          const generatedRow: string[] = [];
          const uploadedThumbnailRow: string[] = [];
          const generatedThumbnailRow: string[] = [];

          (
            batch_image.upload_types as (
              | "RAW_BEFORE"
              | "RAW_AFTER"
              | "CONCAT"
            )[]
          ).forEach((type: string, index: number) => {
            const url = batch_image.urls[index] || "";
            const thumbnailUrl = batch_image.thumbnail_urls?.[index] || url;
            if (type === "RAW_BEFORE" || type === "RAW_AFTER") {
              uploadedRow.push(url);
              uploadedThumbnailRow.push(thumbnailUrl);
            } else if (type === "CONCAT") {
              generatedRow.push(url);
              generatedThumbnailRow.push(thumbnailUrl);
            }
          });

          acc[formattedDate].uploaded.push(uploadedRow);
          acc[formattedDate].generated.push(generatedRow);
          acc[formattedDate].uploadedThumbnails.push(uploadedThumbnailRow);
          acc[formattedDate].generatedThumbnails.push(generatedThumbnailRow);
          acc[formattedDate].batch_uuid.push(batch_image.batch_uuid);
          acc[formattedDate].patient_uuid.push(
            batch_image.patient_uuid ?? null,
          );
          acc[formattedDate].patient_name.push(
            batch_image.patient_uuid
              ? (patientNameByUuid.get(batch_image.patient_uuid) ?? null)
              : null,
          );
          acc[formattedDate].patient_info.push(
            batch_image.patient_uuid
              ? (patientByUuid.get(batch_image.patient_uuid) ?? null)
              : null,
          );

          return acc;
        },
        {} as Record<
          string,
          {
            date_created: string;
            uploaded: string[][];
            generated: string[][];
            uploadedThumbnails: string[][];
            generatedThumbnails: string[][];
            batch_uuid: string[];
            patient_uuid: (string | null)[];
            patient_name: (string | null)[];
            patient_info: (TPatientInfo | null)[];
          }
        >,
      );

      const aggregatedCreatedDateArray = Object.values(aggregatedCreatedDate);
      const nextPatientInfoByBatchUuid: Record<string, TPatientInfo | null> =
        {};
      aggregatedCreatedDateArray.forEach((section) => {
        section.batch_uuid.forEach((batchUuid, index) => {
          nextPatientInfoByBatchUuid[batchUuid] = section.patient_info[index];
        });
      });
      setPatientInfoByBatchUuid(nextPatientInfoByBatchUuid);
      console.log("Aggregated Gallery Data:", aggregatedCreatedDateArray);

      setGalleryData(aggregatedCreatedDateArray);
    } catch (error) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageFromUrl = parseInt(params.get("page") || "0", 10);
    fetchData(pageFromUrl);
  }, []);

  const handlePageClick = async (event: any) => {
    const page = event.selected;
    fetchData(page);
  };

  const closeModal = () => {
    setModalVisible(false);
    setLoadingModal(false);
    setErrorModal("");
    setSelectedModalPatientInfo(null);
  };

  const handlePressSection = async (
    batchUUID: string,
    imageOption: "Generated" | "Uploaded",
  ) => {
    setModalVisible(true);
    setLoadingModal(true);
    setSelectedBatchUUID(batchUUID);
    setSelectedImageOption(imageOption);
    setSelectedModalPatientInfo(patientInfoByBatchUuid[batchUUID] ?? null);
    try {
      if (!session) return;

      const response: TGetBeforeAfterResponse = await getBeforeAfter(session, {
        batch_uuid: batchUUID,
        is_uploaded: true,
      });

      // TODO: Simplify this
      const result = response.batch_images.sort((a, b) => {
        return (
          new Date(b.date_created).getTime() -
          new Date(a.date_created).getTime()
        );
      });

      const aggregatedCreatedDate = result.reduce(
        (acc, batch_image) => {
          const date = new Date(batch_image.date_created);
          const formattedDate = date.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          if (!acc[formattedDate]) {
            acc[formattedDate] = {
              date_created: formattedDate,
              uploaded: [],
              generated: [],
              batch_uuid: [],
            };
          }

          const uploadedRow: string[] = [];
          const generatedRow: string[] = [];

          (
            batch_image.upload_types as (
              | "RAW_BEFORE"
              | "RAW_AFTER"
              | "CONCAT"
            )[]
          ).forEach((type: string, index: number) => {
            const url = batch_image.urls[index] || "";
            if (type === "RAW_BEFORE" || type === "RAW_AFTER") {
              uploadedRow.push(url);
            } else if (type === "CONCAT") {
              generatedRow.push(url);
            }
          });

          acc[formattedDate].uploaded.push(uploadedRow);
          acc[formattedDate].generated.push(generatedRow);
          acc[formattedDate].batch_uuid.push(batch_image.batch_uuid);

          return acc;
        },
        {} as Record<
          string,
          {
            date_created: string;
            uploaded: string[][];
            generated: string[][];
            batch_uuid: string[];
          }
        >,
      );

      const aggregatedCreatedDateArray = Object.values(aggregatedCreatedDate);
      if (imageOption === "Generated") {
        setSelectedImages(aggregatedCreatedDateArray[0].generated[0]);
      } else {
        setSelectedImages(aggregatedCreatedDateArray[0].uploaded[0]);
      }
    } catch (error) {
      setErrorModal("Failed to get images. Please try again.");
    } finally {
      setLoadingModal(false);
    }
  };

  const buildBatchUrlGroups = (batchImage: {
    upload_types: ("RAW_BEFORE" | "RAW_AFTER" | "CONCAT")[];
    urls: string[];
  }) => {
    const uploaded: string[] = [];
    const generated: string[] = [];

    batchImage.upload_types.forEach((type, index) => {
      const url = batchImage.urls[index] || "";
      if (type === "RAW_BEFORE" || type === "RAW_AFTER") {
        uploaded.push(url);
      } else if (type === "CONCAT") {
        generated.push(url);
      }
    });

    return { uploaded, generated };
  };

  const fetchFullSizeBatchUrls = async (batchUUID: string) => {
    if (!session) return null;

    const response: TGetBeforeAfterResponse = await getBeforeAfter(session, {
      batch_uuid: batchUUID,
      is_uploaded: true,
    });

    const batchImage = response.batch_images
      .filter((item) => item.batch_uuid === batchUUID)
      .sort(
        (a, b) =>
          new Date(b.date_created).getTime() -
          new Date(a.date_created).getTime(),
      )[0];

    if (!batchImage) return null;

    return buildBatchUrlGroups(batchImage);
  };

  const downloadImageUrl = async (imageUrl: string) => {
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error("Network response was not ok");

    const blob = await response.blob();
    const link = document.createElement("a");
    const urlObject = URL.createObjectURL(blob);
    link.href = urlObject;
    link.setAttribute("download", `${createUUID()}.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(urlObject);
  };

  const handleOnClickDownloadAll = async (
    batchUUID: string,
    imageOption: "Generated" | "Uploaded",
  ) => {
    try {
      const fullSizeUrls = await fetchFullSizeBatchUrls(batchUUID);
      const urls =
        imageOption === "Generated"
          ? (fullSizeUrls?.generated ?? [])
          : (fullSizeUrls?.uploaded ?? []);

      const downloadPromises = urls.map(async (imageUrl) => {
        await downloadImageUrl(imageUrl);
      });

      await Promise.all(downloadPromises);
    } catch (error) {
      console.error("Error downloading images:", error);
    }
  };

  const handleOnClickDownloadOne = async (
    batchUUID: string,
    imageOption: "Generated" | "Uploaded",
    imageIndex: number,
  ) => {
    try {
      const fullSizeUrls = await fetchFullSizeBatchUrls(batchUUID);
      const urls =
        imageOption === "Generated"
          ? (fullSizeUrls?.generated ?? [])
          : (fullSizeUrls?.uploaded ?? []);
      const imageUrl = urls[imageIndex];

      if (!imageUrl) {
        throw new Error("No image URL available for download");
      }

      await downloadImageUrl(imageUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  const openAssignModal = (batchUuid: string) => {
    setAssignTargetBatchUuid(batchUuid);
    setIsAssignModalVisible(true);
  };

  const handleAssignToPatient = async (patient: TPatientInfo) => {
    if (!session || !assignTargetBatchUuid) return;
    try {
      mixpanel.track("Assign Image To Patient Triggered", {
        "Clinic Name": session?.clinicName ?? "clinic not found",
        Flow: "BeforeAfterGallery",
        Endpoint:
          "/clinics/v1/side-by-side/{clinic_id}/before-after/batch/{batch_uuid}/assign-patient",
        "Batch UUID": assignTargetBatchUuid,
        "Patient UUID": patient.uuid,
      });
      await assignPatientToSideBySideBatch(
        session,
        assignTargetBatchUuid,
        patient.uuid,
      );
      Alert.alert("Success", "Image assigned to patient.");
      await fetchData(selectedPage);
    } catch (error) {
      Alert.alert("Assign failed", "Failed to assign image. Please try again.");
      throw error;
    }
  };

  const deletePresignedUrls = async (presignedUrls: string[]) => {
    if (!session) return;
    if (!presignedUrls.length) return;
    try {
      const uniqueUrls = Array.from(new Set(presignedUrls));
      const deletePromises = uniqueUrls.map(async (presignedUrl) => {
        const s3Key = getS3KeyFromPresignedUrlForGallery(presignedUrl);
        await deleteProgressPhoto(session, s3Key);
      });

      await Promise.all(deletePromises);
      await fetchData(selectedPage);
    } catch (error) {
      Alert.alert(
        "Delete failed",
        "Failed to delete image(s). Please try again.",
      );
      throw error;
    }
  };

  const handleDeleteBatch = async (batchUUID: string) => {
    if (!session) return;
    if (Platform.OS === "web") {
      const shouldDelete = window.confirm("Delete all images in this row?");
      if (!shouldDelete) return;

      try {
        const fullSizeUrls = await fetchFullSizeBatchUrls(batchUUID);
        if (!fullSizeUrls) return;

        await deletePresignedUrls([
          ...fullSizeUrls.uploaded,
          ...fullSizeUrls.generated,
        ]);
        mixpanel.track("Image Batch Deleted", {
          "Clinic Name": session?.clinicName ?? "clinic not found",
          "Batch UUID": batchUUID,
        });
      } catch {
        // Alert is handled in deletePresignedUrls
      }
      return;
    }

    Alert.alert("Delete batch", "Delete all images in this row?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          try {
            const fullSizeUrls = await fetchFullSizeBatchUrls(batchUUID);
            if (!fullSizeUrls) return;

            await deletePresignedUrls([
              ...fullSizeUrls.uploaded,
              ...fullSizeUrls.generated,
            ]);
            mixpanel.track("Image Batch Deleted", {
              "Clinic Name": session?.clinicName ?? "clinic not found",
              "Batch UUID": batchUUID,
            });
          } catch {
            // Alert is handled in deletePresignedUrls
          }
        },
      },
    ]);
  };

  const handleDeleteImageFromModal = async (imageIndex: number) => {
    if (!selectedImages[imageIndex]) return;
    const imageToDelete = selectedImages[imageIndex];

    try {
      await deletePresignedUrls([imageToDelete]);

      setSelectedImages((prev) => {
        const next = prev.filter((_, index) => index !== imageIndex);
        if (next.length === 0) {
          closeModal();
        }
        return next;
      });
      mixpanel.track("Image Deleted", {
        "Clinic Name": session?.clinicName ?? "clinic not found",
        Source: "Fullscreen Viewer",
      });
    } catch {
      // Alert is handled inside deletePresignedUrls
    }
  };

  const ThumbnailImage = ({
    thumbnailUri,
    fullSizeUri,
  }: {
    thumbnailUri: string;
    fullSizeUri: string;
  }) => {
    const [hasError, setHasError] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);

    return (
      <>
        {!hasError && (
          <Image
            source={{ uri: thumbnailUri }}
            style={styles.image}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setHasError(true);
              setIsLoading(false);
            }}
          />
        )}
        {hasError && (
          <Image source={{ uri: fullSizeUri }} style={styles.image} />
        )}
      </>
    );
  };

  const renderImage = ({
    thumbnailUrl,
    fullSizeUrl,
    index,
    batchUUID,
    imageOption,
  }: {
    thumbnailUrl: string;
    fullSizeUrl: string;
    index: number;
    batchUUID: string;
    imageOption: "Generated" | "Uploaded";
  }) => {
    return (
      <View style={[styles.imageContainer]}>
        <ThumbnailImage thumbnailUri={thumbnailUrl} fullSizeUri={fullSizeUrl} />
        <View style={styles.imageActionRow}>
          <TouchableOpacity
            onPress={(e: any) => {
              e?.stopPropagation?.();
              handleOnClickDownloadOne(batchUUID, imageOption, index);
            }}
            style={styles.imageActionButton}
          >
            <Ionicons
              name="download-outline"
              size={20}
              color="#FFFFFF"
              style={styles.actionIconShadow}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderSection = (section: {
    batch_uuid: string[];
    patient_uuid: (string | null)[];
    patient_name: (string | null)[];
    patient_info: (TPatientInfo | null)[];
    date_created: string;
    uploaded: string[][];
    generated: string[][];
    uploadedThumbnails: string[][];
    generatedThumbnails: string[][];
  }) => {
    // Determine visibility for the entire section
    const isSectionVisible =
      imageDisplayOption === "All Images" ||
      (imageDisplayOption === "Generated" &&
        section.generated.some((item) => item.length > 0)) ||
      (imageDisplayOption === "Uploaded" &&
        section.uploaded.some((item) => item.length > 0));

    return (
      <View
        style={[
          styles.sectionContainer,
          {
            display: isSectionVisible ? "flex" : "none",
          },
        ]}
        key={section.date_created}
      >
        <NormalText text={section.date_created} />
        {section.batch_uuid
          .map((_, index) => index)
          .reverse()
          .map((index) => {
            const isGeneratedVisible =
              ["All Images", "Generated"].includes(imageDisplayOption) &&
              section.generated[index].length > 0;

            const isUploadedVisible =
              ["All Images", "Uploaded"].includes(imageDisplayOption) &&
              section.uploaded[index].length > 0;
            const showBatchDeleteOnGenerated = isGeneratedVisible;
            const showBatchDeleteOnUploaded =
              !isGeneratedVisible && isUploadedVisible;

            return (
              <View style={styles.generatedUploadContainer} key={index}>
                {section.patient_name[index] && (
                  <NormalText
                    text={`Patient: ${section.patient_name[index]}`}
                    style={styles.patientNameLabel}
                  />
                )}
                {/* Generated Images */}
                {isGeneratedVisible && (
                  <TouchableOpacity
                    onPress={() =>
                      handlePressSection(section.batch_uuid[index], "Generated")
                    }
                    style={{ position: "relative" }}
                  >
                    <View style={styles.sectionActionRow}>
                      <TouchableOpacity
                        style={styles.downloadAllButton}
                        onPress={(e: any) => {
                          e?.stopPropagation?.();
                          openAssignModal(section.batch_uuid[index]);
                        }}
                      >
                        <Ionicons
                          name="person-add-outline"
                          size={18}
                          color="#17191D"
                        />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.downloadAllButton}
                        onPress={(e: any) => {
                          e?.stopPropagation?.();
                          handleOnClickDownloadAll(
                            section.batch_uuid[index],
                            "Generated",
                          );
                        }}
                      >
                        <Download color={"#000"} />
                      </TouchableOpacity>
                      {showBatchDeleteOnGenerated && (
                        <TouchableOpacity
                          style={styles.downloadAllButton}
                          onPress={(e: any) => {
                            e?.stopPropagation?.();
                            handleDeleteBatch(section.batch_uuid[index]);
                          }}
                          accessibilityRole="button"
                          accessibilityLabel="Delete this batch"
                        >
                          <Ionicons
                            name="trash-outline"
                            size={20}
                            color="#B42318"
                          />
                        </TouchableOpacity>
                      )}
                    </View>
                    <NormalText
                      text="Generated"
                      style={{
                        color: "#515561",
                      }}
                    />
                    <FlatList
                      data={section.generated[index]}
                      renderItem={({ item, index: imageIndex }) =>
                        renderImage({
                          thumbnailUrl:
                            section.generatedThumbnails[index][imageIndex],
                          fullSizeUrl: item,
                          index: imageIndex,
                          batchUUID: section.batch_uuid[index],
                          imageOption: "Generated",
                        })
                      }
                      keyExtractor={(item, index) => `${index}`}
                      horizontal
                      showsHorizontalScrollIndicator={!isMobile}
                      ItemSeparatorComponent={() => (
                        <View style={styles.separator} />
                      )}
                    />
                  </TouchableOpacity>
                )}

                {/* Uploaded Images */}
                {isUploadedVisible && (
                  <TouchableOpacity
                    onPress={() =>
                      handlePressSection(section.batch_uuid[index], "Uploaded")
                    }
                    style={{ position: "relative" }}
                  >
                    <View style={styles.sectionActionRow}>
                      {!isGeneratedVisible && (
                        <TouchableOpacity
                          style={styles.downloadAllButton}
                          onPress={(e: any) => {
                            e?.stopPropagation?.();
                            openAssignModal(section.batch_uuid[index]);
                          }}
                        >
                          <Ionicons
                            name="person-add-outline"
                            size={18}
                            color="#17191D"
                          />
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity
                        style={styles.downloadAllButton}
                        onPress={(e: any) => {
                          e?.stopPropagation?.();
                          handleOnClickDownloadAll(
                            section.batch_uuid[index],
                            "Uploaded",
                          );
                        }}
                      >
                        <Download color={"#17191D"} />
                      </TouchableOpacity>
                      {showBatchDeleteOnUploaded && (
                        <TouchableOpacity
                          style={styles.downloadAllButton}
                          onPress={(e: any) => {
                            e?.stopPropagation?.();
                            handleDeleteBatch(section.batch_uuid[index]);
                          }}
                          accessibilityRole="button"
                          accessibilityLabel="Delete this batch"
                        >
                          <Ionicons
                            name="trash-outline"
                            size={20}
                            color="#B42318"
                          />
                        </TouchableOpacity>
                      )}
                    </View>
                    <NormalText
                      text="Uploaded"
                      style={{
                        color: "#515561",
                      }}
                    />
                    <FlatList
                      data={section.uploaded[index]}
                      renderItem={({ item, index: imageIndex }) =>
                        renderImage({
                          thumbnailUrl:
                            section.uploadedThumbnails[index][imageIndex],
                          fullSizeUrl: item,
                          index: imageIndex,
                          batchUUID: section.batch_uuid[index],
                          imageOption: "Uploaded",
                        })
                      }
                      keyExtractor={(item, index) => `${index}`}
                      horizontal
                      showsHorizontalScrollIndicator={!isMobile}
                      ItemSeparatorComponent={() => (
                        <View style={styles.separator} />
                      )}
                    />
                  </TouchableOpacity>
                )}
              </View>
            );
          })}
      </View>
    );
  };
  if (error) return <NormalText text={error} />;

  const selectedPatientCreatedAt = selectedModalPatientInfo?.createdAt
    ? new Date(selectedModalPatientInfo.createdAt).toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  return (
    <View style={{ position: "relative", flex: 1 }}>
      <Modal visible={modalVisible} transparent animationType={"fade"}>
        <Pressable
          style={styles.modalContainer}
          onPress={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <View style={styles.modalInnerContainer}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => {
                closeModal();
              }}
            >
              <Xv2 />
            </TouchableOpacity>
            {selectedModalPatientInfo && (
              <View style={styles.modalPatientInfoContainer}>
                <NormalText
                  text={`Patient: ${selectedModalPatientInfo.firstName || ""} ${
                    selectedModalPatientInfo.lastName || ""
                  }`.trim()}
                  style={styles.modalPatientInfoText}
                />
                <NormalText
                  text={`Email: ${selectedModalPatientInfo.email || "-"}`}
                  style={styles.modalPatientInfoText}
                />
                <NormalText
                  text={`Phone: ${selectedModalPatientInfo.phone || "-"}`}
                  style={styles.modalPatientInfoText}
                />
                <NormalText
                  text={`Created: ${selectedPatientCreatedAt || "-"}`}
                  style={styles.modalPatientInfoText}
                />
              </View>
            )}
            <ImageCarousel
              images={selectedImages || []}
              showDownloadAllButton
              showDownloadOneButton
              showDeleteButton
              loading={loadingModal}
              error={errorModal}
              onDownloadAll={
                selectedBatchUUID
                  ? () =>
                      handleOnClickDownloadAll(
                        selectedBatchUUID,
                        selectedImageOption,
                      )
                  : undefined
              }
              onDownloadOne={
                selectedBatchUUID
                  ? (imageIndex) =>
                      handleOnClickDownloadOne(
                        selectedBatchUUID,
                        selectedImageOption,
                        imageIndex,
                      )
                  : undefined
              }
              onDeleteOne={handleDeleteImageFromModal}
            />
          </View>
        </Pressable>
      </Modal>
      <AssignImageToPatientModal
        visible={isAssignModalVisible}
        onClose={() => setIsAssignModalVisible(false)}
        onAssign={handleAssignToPatient}
      />

      <View style={styles.titleContainer}>
        <TitleText
          text={isMobile ? "Gallery" : "Before-After Gallery"}
          style={{ marginTop: 5 }}
          numberOfLines={1}
        />
        <View style={{ width: 160 }}>
          <InputDropdown
            name={""}
            value={imageDisplayOption}
            selections={["Generated", "Uploaded", "All Images"]}
            onSelect={(value) => setImageDisplayOption(value)}
          />
        </View>
      </View>
      {loading ? (
        <ActivityIndicator
          style={{ marginTop: 50 }}
          size="large"
          color="#00B7E0"
        />
      ) : (
        <ScrollView
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
        >
          {galleryData.length === 0 && <GalleryEmpty />}
          {galleryData.length > 0 && (
            <>
              <View>{galleryData.map(renderSection)}</View>
              <Paginate
                pageCount={pageCount}
                selectedPage={selectedPage}
                handlePageClick={handlePageClick}
              />
            </>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  titleContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    zIndex: 1,
  },
  sectionContainer: {
    gap: 10,
    marginTop: 10,
  },
  imageContainer: {
    position: "relative",
    borderRadius: 5,
    overflow: "hidden",
    elevation: 2,
    backgroundColor: "#ccc",
    borderWidth: 1,
    borderColor: "#ccc",
  },
  image: {
    width: 125,
    height: 125,
    resizeMode: "cover",
  },
  imageActionRow: {
    position: "absolute",
    bottom: 4,
    right: 4,
    flexDirection: "row",
    gap: 4,
  },
  imageActionButton: {
    width: 30,
    height: 30,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 6,
  },
  actionIconShadow: {
    textShadow: "0px 1px 4px rgba(0,0,0,0.95)",
  } as any,
  separator: {
    width: 7,
  },
  generatedUploadContainer: {
    borderWidth: 1,
    borderRadius: 10,
    borderColor: "#DDDEE0",
    padding: 10,
    gap: 10,
  },
  patientNameLabel: {
    color: "#17191D",
    fontSize: 13,
  },
  modalPatientInfoContainer: {
    borderWidth: 1,
    borderColor: "#DDDEE0",
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    gap: 4,
  },
  modalPatientInfoText: {
    color: "#17191D",
    fontSize: 13,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    padding: 16,
    // @ts-ignore
    cursor: Platform.OS === "web" ? "default" : "auto",
  },
  modalInnerContainer: {
    margin: "auto",
    width: "100%",
    height: "100%",
    maxWidth: 1000,
    maxHeight: 1000,
    backgroundColor: "#FFF",
    borderRadius: 5,
    padding: 10,
    zIndex: 2,
  },
  closeButton: {
    marginLeft: "auto",
    right: -5,
    width: 30,
    height: 30,
  },
  sectionActionRow: {
    position: "absolute",
    top: -18,
    right: -18,
    zIndex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  downloadAllButton: {
    width: 50,
    height: 50,
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
  },
});

export default Gallery;
